/* eslint-disable no-undefined */
import { isNil } from 'lodash';

import type { Maybe } from '../globals';

const isDefined = <T>(value: Maybe<T>): value is T =>
  value !== null && value !== undefined && typeof value !== 'undefined';

/**
 * Evaluates whether some or all elements in an array satisfy a given predicate.
 *
 * This function performs two checks:
 * 1. Determines if at least one element in the array satisfies the predicate (`some`).
 * 2. Determines if all elements in the array satisfy the predicate (`every`).
 *
 * @template T - The type of elements in the array.
 * @param array - The array to evaluate.
 * @param predicate - A function that takes an element of the array and returns
 * a boolean indicating whether the element satisfies the condition.
 * Defaults to `isDefined`, which checks if a value is neither `null` nor `undefined`.
 * @returns A tuple:
 * - The first value (`some`) is `true` if at least one element satisfies the predicate, otherwise `false`.
 * - The second value (`every`) is `true` if all elements satisfy the predicate, otherwise `false`.
 *
 * @example
 * // Example 1: Mixed array with null values
 * const array = [1, null, 3];
 * const result = someAndEvery(array);
 * console.log(result); // Output: [true, false]
 *
 * @example
 * // Example 2: Array with all positive numbers
 * const array = [1, 2, 3];
 * const result = someAndEvery(array, (value) => value > 0);
 * console.log(result); // Output: [true, true]
 *
 * @example
 * // Example 3: Array with all non-positive numbers
 * const array = [0, -1, -2];
 * const result = someAndEvery(array, (value) => value > 0);
 * console.log(result); // Output: [false, false]
 */
export function someAndEvery<T>(
  array: T[],
  predicate: (value: T) => boolean = isDefined
): [some: boolean, every: boolean] {
  let [some, every] = [false, true];

  for (const value of array) {
    const current = predicate(value);
    some ||= current;
    every &&= current;
    if (some && !every) {
      break;
    }
  }

  return [some, every];
}

/**
 * Converts an array with a single object containing numeric keys to a plain array of values.
 * This handles cases where query parameters are parsed as [{ '0': 'value1', '1': 'value2', ... }]
 * Only applies when the object has more than 20 keys.
 *
 * @param joins - The input array that may contain an object with numeric keys
 * @returns The normalized array, either the original or with values extracted from the object
 *
 * @example
 * // Example 1: Array with single object containing numeric keys (>20 keys)
 * const input = [{ '0': 'user', '1': 'user.company', ..., '21': 'user.permissions' }];
 * const result = convertNumericKeysObjectToArray(input);
 * // Output: ['user', 'user.company', ..., 'user.permissions']
 *
 * @example
 * // Example 2: Regular array (unchanged)
 * const input = ['user', 'user.company'];
 * const result = convertNumericKeysObjectToArray(input);
 * // Output: ['user', 'user.company']
 *
 * @example
 * // Example 3: Object with fewer keys (unchanged)
 * const input = [{ '0': 'user', '1': 'company' }];
 * const result = convertNumericKeysObjectToArray(input);
 * // Output: [{ '0': 'user', '1': 'company' }]
 */
export function convertNumericKeysObjectToArray(joins: any[]): any[] {
  if (
    Array.isArray(joins) &&
    joins.length === 1 &&
    typeof joins[0] === 'object' &&
    joins[0] !== null &&
    !Array.isArray(joins[0]) &&
    Object.keys(joins[0]).length > 20
  ) {
    return Object.values(joins[0]);
  }
  return joins;
}

/**
 * Sliding windows of `size` consecutive elements from `arr`.
 *
 * Same behavior as the former `each-cons` npm package (unmaintained); kept in-repo
 * so we do not depend on that package for typings or security updates.
 *
 * @example
 * // Pairs of neighbors (common for “each consecutive pair” checks):
 * eachCons([1, 2, 3, 4], 2);
 * // => [[1, 2], [2, 3], [3, 4]]
 *
 * @example
 * // Window larger than the array → no windows:
 * eachCons([1, 2], 3);
 * // => []
 *
 * @example
 * // When `size` is less than 1, returns no windows:
 * eachCons([1, 2, 3], 0);
 * // => []
 */
export function eachCons<T>(arr: readonly T[], size: number): T[][] {
  if (size < 1 || arr.length < size) {
    return [];
  }

  const result: T[][] = [];
  for (let i = 0; i <= arr.length - size; i++) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

export function toArray<T>(value?: Maybe<T | T[]>): T[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (isNil(value) || typeof value === 'undefined') {
    return [];
  }

  return [value];
}

/**
 * Sorts `items` so that entries matching `pinnedKeys` come first, in the order given by
 * `pinnedKeys`, followed by the remaining entries. Does not mutate the input array.
 *
 * @param items - The items to sort.
 * @param options.pinnedKeys - Keys (as returned by `keyFn`) to pin to the top, in priority order.
 * @param options.keyFn - Extracts the comparable key for an item.
 * @param options.compareRest - Comparator used to order the non-pinned remainder. Defaults to
 * preserving their original relative order.
 *
 * @example
 * // Pin USA and Canada above the rest, alphabetized:
 * sortByPinned(countryOptions, {
 *   pinnedKeys: ['USA', 'CAN'],
 *   keyFn: (option) => option.value,
 *   compareRest: (a, b) => a.label.localeCompare(b.label)
 * });
 */
export function sortByPinned<T, K>(
  items: readonly T[],
  options: {
    pinnedKeys: readonly K[];
    keyFn: (item: T) => K;
    compareRest?: (a: T, b: T) => number;
  }
): T[] {
  const { pinnedKeys, keyFn, compareRest } = options;

  const pinnedOrder = new Map<K, number>();
  pinnedKeys.forEach((key, index) => pinnedOrder.set(key, index));

  const pinned: T[] = [];
  const rest: T[] = [];

  for (const item of items) {
    (pinnedOrder.has(keyFn(item)) ? pinned : rest).push(item);
  }

  pinned.sort((a, b) => pinnedOrder.get(keyFn(a))! - pinnedOrder.get(keyFn(b))!);
  if (compareRest) {
    rest.sort(compareRest);
  }

  return [...pinned, ...rest];
}
